<template>
  <div class="border border-gray-700 rounded-xs p-5 pb-10">
    <div class="px-5 py-3" v-if="impactAreaOptions.length > 1">
      <h2 class="text-base font-bold leading-5 m-0 mb-1 p-1 uppercase" >Select Impact Areas</h2>
      <SelectionChip
        v-if="impactAreaOptions.length > 1"
        :items="impactAreaOptions"
        :model-value="selectedImpactAreas"
        item-key="area_uuid"
        item-label="area_label"
        :show-icon="true"
        @update:model-value="handleImpactAreaClick"
      />
      <hr class="border-gray-400 mx-5 mt-4" />
    </div>
    
    <div class="mt-4">
      <div v-if="loading || isLoadingStatement" class="flex flex-col justify-center items-center gap-4 min-h-[200px]">
        <ProgressSpinner class="h-8 w-8 stroke-primary-400" strokeWidth="4" />
        <p>Generating impact statement...</p>
      </div>
      
      <GbInlineMessage v-else-if="statementGenerationError" gbTheme="red" class="mb-4">
        <template #icon></template>
        <div class="inline-flex items-center">
          <span class="material-icons-round text-red-300 mr-2 self-start" aria-hidden="true">warning_amber</span>
          <div>
            <p class="font-bold">Error generating statement</p>
            <p class="text-sm leading-5">We're having trouble generating your impact statement. Please check your internet connection and reload the page to try again.</p>
          </div>
        </div>
      </GbInlineMessage>
      
      <WikiTextEditor
        v-else-if="!isStatementEmptyPlaceholder"
        v-model:wikiText="statement"
        :toEmbeddedText="toEmbeddedText"
        :toWikiText="toWikiText"
        :outer-class="'rounded-md pt-3 px-6 pb-3 mb-3 white-mode text-xl'"
        :placeHolder="'Generating impact statement...'"
        :reset-value="unEditedStatement"
        edited-text-type="iodstatement"
        @editModeUpdated="handleEditModeUpdated"
      >
        <template #bottomRightContent>
          <div v-if="savedFolderError" class="text-md flex items-center text-red-300 mr-1">
            <span class="material-icons-round">warning_amber</span>
            <span>Save failed.</span>
          </div>
          <div class="flex flex-col gap-2">
            <GbButton
              gbTheme="greenPrimary"
              size="small"
              @click="openSaveModal"
            >
            <span class="flex items-center">  
              <span class="material-icons-round text-lg pr-1">folder</span>
              <span>Save to Folder</span>
            </span>
            </GbButton>

            <ContentReviewButton
              v-if="showReviewButton"
              :criteria-set-name="reviewCriteriaSetName"
              :has-existing-results="hasResults"
              @click="handleAutoReviewButtonClick"
            />
          </div>
        </template>
      </WikiTextEditor>
      
      <div v-else class="p-8 text-center">
        <p class="text-xl italic">{{ statement }}</p>
      </div>
    </div>

    <ContentReviewResults
      v-if="hasResults || isReviewing || reviewError"
      title="Impact Statement Review"
      :results="reviewResults"
      :is-loading="isReviewing"
      :error="reviewError"
      :expected-count="impactCriteria.length"
      @close="handleCloseReviewResults"
      @toggle="handleReviewResultsToggle"
    />

    <ContentReviewModal
      :open="showReviewModal"
      :criteria="impactCriteria"
      :criteria-set-name="reviewCriteriaSetName"
      content-label="Impact of Disability"
      :subjects="props.subjects"
      @update:open="showReviewModal = $event"
      @review="handleReview"
    />

    <SaveToFolderModal
      v-if="showSaveToFolderModal"
      content-type="saved_impact_of_disability"
      content-type-label="Impact of Disability Statement"
      :content-payload="statementPayload"
      :post-save-navigate="true"
      @update:visible="(val) => { showSaveToFolderModal = val }"
      @save="handleSaveToFolder"
    />

    <Dialog :open="showConfirmDialog" @update:open="(val) => { if (!val) cancelImpactAreaChange() }">
      <DialogContent class="sm:max-w-md rounded-lg bg-white shadow-xl p-0">
        <DialogHeader class="py-3 px-5 border-b border-gray-300">
          <DialogTitle class="text-xl font-medium text-gray-900">Confirm Impact Area Change</DialogTitle>
        </DialogHeader>
        <div class="pr-10 pl-5 py-5 text-gray-700">
          <p>If you make this selection, your edits will be lost.</p><br/>
          <p>Do you want to navigate away from your edited statement?</p>
        </div>
        <DialogFooter class="px-5 py-4 border-t border-gray-300 gap-3 justify-end">
          <GbButton
            gbTheme="grayOutlineSecondary"
            @click="cancelImpactAreaChange"
          >
            No
          </GbButton>
          <GbButton
            gbTheme="bluePrimary"
            @click="confirmImpactAreaChange"
          >
            Yes
          </GbButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useContentReviewStore } from '@/content-review/content-review.store'
import { useContentReview } from '@/content-review/composables/useContentReview'
import { reviewImpactStatementStream } from '@/content-review/api'
import ContentReviewButton from '@/content-review/components/ContentReviewButton.vue'
import ContentReviewModal from '@/content-review/components/ContentReviewModal.vue'
import ContentReviewResults from '@/content-review/components/ContentReviewResults.vue'
import { useRouter, useRoute } from 'vue-router'
import SelectionChip from '@/components/SelectionChip.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import ProgressSpinner from 'primevue/progressspinner'
import { generateIODStatements } from './api'
import { buildIODStatement } from './statement-builder'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { IMPACT_OF_DISABILITY_EVENT_NAMES } from './event_names'
import { useImpactStatementReviewAnalytics } from './composables/useImpactStatementReviewAnalytics'
import type { ImpactArea, StatementForDisability } from './impact-of-disability_types'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import GbButton from '@/components/GbButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/dialog'
import { useLibraryStore } from '@/library/library.store'
import { useImpactOfDisabilityStore } from './impact-of-disability.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { transformWikiToResourceComponents, transformResourceComponentsToWiki } from '@/components/editor/wiki_tiptap_helpers'

interface Props {
  disability: string
  disabilityLabel: string
  subjects: string[]
  impactAreas: ImpactArea[]
  impactAreaOptions: ImpactArea[]
  changeRequiresConfirmation?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  changeRequiresConfirmation: false,
  loading: false
})

const emit = defineEmits<{
  'update:impactAreas': [value: ImpactArea[]]
  'update:changeRequiresConfirmation': [value: boolean]
  'confirmupdate:impactAreas': [value: ImpactArea[]]
}>()

const router = useRouter()
const route = useRoute()
const analytics = getAnalyticsTracker()
const libraryStore = useLibraryStore()
const iodStore = useImpactOfDisabilityStore()
const contentProfileStore = useContentProfileStore()
const visibilityStore = useVisibilityStore()
const contentReviewStore = useContentReviewStore()

const { isReviewing, reviewResults, reviewError, hasResults, startReview, clearResults } =
  useContentReview()
const showReviewModal = ref(false)

const impactCriteria = computed(() =>
  contentReviewStore.impactCriteriaForProfile(
    contentProfileStore.profileId,
    visibilityStore.betaVisibility
  )
)
const showReviewButton = computed(() => visibilityStore.betaVisibility && impactCriteria.value.length > 0 && !isStatementEmptyPlaceholder.value)
const reviewCriteriaSetName = computed(() =>
  contentReviewStore.criteriaSetName(contentProfileStore.profileId, visibilityStore.betaVisibility)
)
const criteriaEventAttrs = computed(() => ({
  criteria_label: reviewCriteriaSetName.value || 'default',
  is_custom_criteria_set: contentReviewStore.isCustomCriteriaSet(contentProfileStore.profileId, visibilityStore.betaVisibility),
}))

const {
  handleAutoReviewButtonClick: _handleAutoReviewButtonClick,
  handleModalReview,
  handleCloseReviewResults: _handleCloseReviewResults,
  handleReviewResultsToggle,
  handleReceivedReviewResults,
} = useImpactStatementReviewAnalytics(analytics, criteriaEventAttrs, hasResults)

function handleAutoReviewButtonClick() {
  _handleAutoReviewButtonClick(() => { showReviewModal.value = true })
}

function handleCloseReviewResults() {
  _handleCloseReviewResults(() => { clearResults() })
}

function handleReview() {
  handleModalReview(() => {
    showReviewModal.value = false
    startReview(
      ({ signal, onProgress }) =>
        reviewImpactStatementStream({
          impactStatementText: statement.value,
          contentProfile: contentProfileStore.profileId,
          subjects: props.subjects,
          betaMode: visibilityStore.betaVisibility,
          signal,
          onProgress,
        }),
      handleReceivedReviewResults
    )
  })
}

const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki

const selectedImpactAreas = ref<ImpactArea[]>([...props.impactAreas])
const statement = ref('')
const unEditedStatement = ref('')
const isStatementEmptyPlaceholder = ref(false)
const isLoadingStatement = ref(false)
const showSaveToFolderModal = ref(false)
const savedFolderError = ref(false)
const statementGenerationError = ref(false)
const showConfirmDialog = ref(false)
const pendingImpactAreas = ref<ImpactArea[]>([])

watch([() => props.disability, () => props.subjects, () => props.impactAreas, () => contentProfileStore.profileId], async ([disability, subjects, impactAreas, profileId], [oldDisability, oldSubjects, oldImpactAreas, oldProfileId]) => {
  // Clear cache when disability or content profile changes to ensure fresh statements
  if (disability !== oldDisability || profileId !== oldProfileId) {
    iodStore.clearCache()
    clearResults()
  }
  
  selectedImpactAreas.value = [...impactAreas]
  await pieceStatementsTogether()
  updateRoute()
}, {deep: true, immediate: true})

onMounted(async () => {
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.VIEWED_RESULTS)
  if (!contentReviewStore.profiles.length) {
    contentReviewStore.fetchProfiles()
  }
})

async function getStatements() {
  statementGenerationError.value = false
  try {
    if (props.subjects.length < 1 || props.disability.length < 1) {
      return []
    }

    // If disability has multiple impact areas (>1) but none are selected, don't make API call
    // Disabilities with only 1 impact area should still generate statements
    if (props.impactAreaOptions.length > 1 && props.impactAreas.length < 1) {
      return []
    }

    // If there's exactly one impact area and none are selected, automatically use it
    const impactAreasToUse = props.impactAreas.length > 0 
      ? props.impactAreas 
      : props.impactAreaOptions.length === 1 
        ? props.impactAreaOptions 
        : []
    
    const impactAreaLabels = impactAreasToUse.map(ia => ia.area_label)
    
    // Get cached statements
    const cachedStatements = iodStore.getCachedStatements(props.disabilityLabel, props.subjects, impactAreaLabels, contentProfileStore.profileId)
    
    // Check if all requested combinations are already cached
    if (iodStore.areAllStatementsCached(props.disabilityLabel, props.subjects, impactAreaLabels, contentProfileStore.profileId)) {
      return transformStatements(cachedStatements)
    }

    // Identify missing combinations that need to be generated
    const missing = iodStore.getMissingCombinations(props.disabilityLabel, props.subjects, impactAreaLabels, contentProfileStore.profileId)
    
    if (missing.subjects.length === 0 || missing.impactAreas.length === 0) {
      // No missing combinations, return cached
      return transformStatements(cachedStatements)
    }

    // Generate only the missing statements via API
    const response = await generateIODStatements({
      disability: props.disabilityLabel,
      subjects: missing.subjects,
      impact_areas: missing.impactAreas,
      content_profile: contentProfileStore.profileId,
      beta_only: visibilityStore.betaVisibility
    })

    // Cache the newly generated statements
    iodStore.cacheGeneratedStatements(props.disabilityLabel, response.statements, contentProfileStore.profileId)

    // Combine cached and newly generated statements
    const allStatements = [...cachedStatements, ...response.statements]
    return transformStatements(allStatements)
  } catch (error) {
    console.error('Error generating IOD statements:', error)
    statementGenerationError.value = true
    return []
  }
}

function transformStatements(statements: any[]): StatementForDisability[] {
  return statements
    .map((stmt): StatementForDisability | null => {
      const matchingImpactArea = props.impactAreaOptions.find(
        (ia) => ia.area_label === stmt.impact_area
      )

      if (!matchingImpactArea) {
        console.warn('No matching impact area for label:', stmt.impact_area)
        return null
      }

      return {
        subject: stmt.subject,
        area_uuid: matchingImpactArea.area_uuid,
        statement_text: stmt.statement_text
      }
    })
    .filter((stmt): stmt is StatementForDisability => stmt !== null)
}

async function pieceStatementsTogether() {
  isLoadingStatement.value = true
  try {
    const statementsForDisability = await getStatements()
    
    // If there's exactly one impact area and none are selected, automatically use it
    const impactAreasToUse = props.impactAreas.length > 0 
      ? props.impactAreas 
      : props.impactAreaOptions.length === 1 
        ? props.impactAreaOptions 
        : []
  
    const [generatedStatement, isStatementEmptyPlaceholderBool] = buildIODStatement({
      statementsForDisability,
      selectedSubjects: props.subjects,
      selectedImpactAreas: impactAreasToUse
    })

      statement.value = generatedStatement
      isStatementEmptyPlaceholder.value = isStatementEmptyPlaceholderBool
      emit('update:changeRequiresConfirmation', false)
      unEditedStatement.value = statement.value
    } finally {
      isLoadingStatement.value = false
    }
}

function updateRoute() {
  const newQuery = {
    ...route.query,
    subjects: props.subjects,
    disability: props.disability,
    impactAreas: props.impactAreas.map(ia => ia.area_uuid)
  }
  
  router.push({
    name: 'ImpactOfDisability',
    query: newQuery
  })
}

function handleImpactAreaClick(newVal: ImpactArea[]) {
  if (props.changeRequiresConfirmation) {
    pendingImpactAreas.value = newVal
    showConfirmDialog.value = true
  } else {
    selectedImpactAreas.value = newVal
    emit('update:impactAreas', newVal)
  }
}

function confirmImpactAreaChange() {
  selectedImpactAreas.value = pendingImpactAreas.value
  emit('update:impactAreas', pendingImpactAreas.value)
  emit('update:changeRequiresConfirmation', false)
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.RESET_STATEMENT_TEXT)
  showConfirmDialog.value = false
}

function cancelImpactAreaChange() {
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.DISMISSED_STATEMENT_RESET)
  showConfirmDialog.value = false
}

function handleEditModeUpdated(isEditMode: boolean) {
  if (!isEditMode) {
    // Strip HTML tags and whitespace for comparison
    const stripHtml = (str: string) => str.replace(/<\/?p>/g, '').trim().replace(/(\s)/gm, '')
    const currentStatement = stripHtml(statement.value)
    const originalStatement = stripHtml(unEditedStatement.value)
    
    if (currentStatement !== originalStatement) {
      emit('update:changeRequiresConfirmation', true)
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.EDITED_STATEMENT, { edited_text: statement.value })
    } else {
      emit('update:changeRequiresConfirmation', false)
    }
  }
}

const statementPayload = computed(() => ({
  disability: props.disabilityLabel,
  subjects: props.subjects,
  impact_areas: props.impactAreas.map(ia => ia.area_label),
  statement: statement.value
}))

function openSaveModal() {
  showSaveToFolderModal.value = true
}

async function handleSaveToFolder(folder: { uuid: string, name: string }) {
  savedFolderError.value = false
  try {
    await libraryStore.fetchFoldersForContentByType('saved_impact_of_disability')
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.SAVED_STATEMENT)
  } catch (error) {
    savedFolderError.value = true
    console.error('Failed to refresh library state after save:', error)
  }
}
</script>

